<div
  class="min-h-screen bg-[#FAFAFA] text-gray-900 font-sans selection:bg-brand/20 pb-safe print:bg-white"
>
  <!-- Loading State -->
  @if (isLoading$ | async) {
    <div class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 mx-auto mb-4" [style.borderTopColor]="themeColor"></div>
        <p class="text-gray-600">Loading lesson plan...</p>
      </div>
    </div>
  } @else if (error$ | async; as error) {
    <!-- Error State -->
    <div class="flex items-center justify-center min-h-screen">
      <div class="text-center max-w-md px-4">
        <div class="text-red-500 text-5xl mb-4">⚠️</div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
        <p class="text-gray-600 mb-6">{{ error }}</p>
        <a href="/lesson-plan" class="inline-block px-6 py-3 rounded-lg text-white font-semibold" [style.backgroundColor]="themeColor">
          Create New Lesson Plan
        </a>
      </div>
    </div>
  } @else if (currentStep$ | async; as currentStep) {
    <!-- Stepper Header (hidden on step 6) -->
    @if (currentStep !== 6) {
      <div class="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-30 print:hidden">
        <div class="max-w-6xl mx-auto px-4 py-4">
          <!-- Desktop View -->
          <div class="hidden md:flex items-center justify-between relative">
            <div class="absolute top-5 -translate-y-1/2 left-5 right-5 h-0.5 bg-gray-200 -z-0"></div>
            <div
              class="absolute top-5 -translate-y-1/2 left-5 h-0.5 transition-all duration-500 ease-in-out -z-0"
              [style.backgroundColor]="themeColor"
              [style.width]="getProgressWidth(currentStep)"
            ></div>

            @for (step of steps; track step.num) {
              <div class="relative z-10 flex flex-col items-center">
                <div
                  class="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-xs md:text-sm transition-all duration-300 border-2"
                  [class.text-white]="currentStep >= step.num"
                  [class.border-transparent]="currentStep >= step.num"
                  [class.shadow-md]="currentStep >= step.num"
                  [class.bg-white]="currentStep < step.num"
                  [class.border-gray-200]="currentStep < step.num"
                  [class.text-gray-400]="currentStep < step.num"
                  [style.backgroundColor]="currentStep >= step.num ? themeColor : 'white'"
                >
                  @if (currentStep > step.num) {
                    <lucide-icon [img]="icons.CheckCircle2" class="w-5 h-5"></lucide-icon>
                  } @else {
                    {{ step.num }}
                  }
                </div>
                <span
                  class="mt-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-300"
                  [class.text-gray-900]="currentStep >= step.num"
                  [class.text-gray-400]="currentStep < step.num"
                >
                  {{ step.title }}
                </span>
              </div>
            }
          </div>

          <!-- Mobile View -->
          <div class="md:hidden flex flex-col gap-2">
            <div class="flex justify-between items-center text-sm font-bold text-gray-800">
              <span>Step {{ currentStep }} of 6</span>
              <span [style.color]="themeColor">{{ getStepTitle(currentStep) }}</span>
            </div>
            <div class="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-500"
                [style.backgroundColor]="themeColor"
                [style.width]="getMobileProgressWidth(currentStep)"
              ></div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Main Content -->
    <main
      class="print:p-0"
      [class.py-6]="currentStep !== 6"
      [class.px-4]="currentStep !== 6"
      [class.md:px-8]="currentStep !== 6"
    >
      @switch (currentStep) {
        @case (1) {
          <app-step-input (next)="onStepNext()"></app-step-input>
        }
        @case (2) {
          <app-step-settings
            (back)="onStepBack()"
            (next)="onStepNext()"
          ></app-step-settings>
        }
        @case (3) {
          <app-step-topic
            (back)="onStepBack()"
            (next)="onStepNext()"
          ></app-step-topic>
        }
        @case (4) {
          <app-step-objectives
            (back)="onStepBack()"
            (next)="onStepNext()"
          ></app-step-objectives>
        }
        @case (5) {
          <app-step-structure
            (back)="onStepBack()"
            (next)="onStepNext()"
          ></app-step-structure>
        }
        @case (6) {
          <app-step-content (back)="onStepBack()">
            <router-outlet></router-outlet>
          </app-step-content>
        }
      }
    </main>
  }
</div>
